import { useState, useRef, useEffect, useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { DataTableColumn, SortConfig, FilterValue, DiscoveredValue } from './types';
import { FilterPopover } from './filters/FilterPopover';

interface DataTableHeaderProps<T> {
  columns: DataTableColumn<T>[];
  columnOrder: string[];
  columnWidths: Record<string, number>;
  hiddenColumns: Set<string>;
  sortConfig: SortConfig | null;
  filters: Record<string, FilterValue>;
  discoveredValues: Record<string, DiscoveredValue[]>;
  selectable?: boolean;
  allSelected?: boolean;
  someSelected?: boolean;
  onSelectAll?: () => void;
  onSort: (column: string) => void;
  onFilter: (column: string, value: FilterValue | null) => void;
  onColumnResize: (column: string, width: number) => void;
  onColumnOrderChange?: (newOrder: string[]) => void;
}

// Sortable column header component
interface SortableColumnHeaderProps<T> {
  column: DataTableColumn<T>;
  columnWidths: Record<string, number>;
  sortConfig: SortConfig | null;
  filters: Record<string, FilterValue>;
  discoveredValues: Record<string, DiscoveredValue[]>;
  openFilter: string | null;
  setOpenFilter: (key: string | null) => void;
  onSort: (column: string) => void;
  onFilter: (column: string, value: FilterValue | null) => void;
  onResizeStart: (e: React.MouseEvent, columnKey: string) => void;
  resizing: string | null;
}

function SortableColumnHeader<T>({
  column,
  columnWidths,
  sortConfig,
  filters,
  discoveredValues,
  openFilter,
  setOpenFilter,
  onSort,
  onFilter,
  onResizeStart,
  resizing,
}: SortableColumnHeaderProps<T>) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    width: columnWidths[column.key] ? `${columnWidths[column.key]}px` : `${column.minWidth ?? 80}px`,
    overflow: 'hidden' as const,
  };

  const hasFilter = !!filters[column.key];
  const sortIndicator = sortConfig?.column === column.key
    ? (sortConfig.order === 'asc' ? '↑' : '↓')
    : null;

  const isFilterable = (col: DataTableColumn<T>) => {
    if (col.filterable === false) return false;
    return ['text', 'number', 'date', 'datetime', 'enum', 'dynamic-enum', 'multi-enum', 'multi-text', 'boolean'].includes(col.type);
  };

  return (
    <th
      ref={setNodeRef}
      style={style}
      className={`relative p-3 text-left font-medium text-sage-700 select-none ${
        column.sortable !== false ? 'cursor-pointer hover:bg-sage-50' : ''
      } ${column.align === 'right' ? 'text-right' : ''} ${column.align === 'center' ? 'text-center' : ''} ${
        isDragging ? 'z-20 bg-sage-100 shadow-lg' : ''
      }`}
    >
      <div className="flex items-center gap-1">
        {/* Drag handle */}
        <span
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 -ml-1 text-sage-400 hover:text-sage-600"
          title="Drag to reorder"
        >
          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="5" cy="5" r="2" />
            <circle cx="12" cy="5" r="2" />
            <circle cx="5" cy="12" r="2" />
            <circle cx="12" cy="12" r="2" />
            <circle cx="5" cy="19" r="2" />
            <circle cx="12" cy="19" r="2" />
          </svg>
        </span>

        <span
          className="truncate flex-1"
          onClick={() => column.sortable !== false && onSort(column.key)}
        >
          {column.label}
        </span>

        {/* Sort indicator */}
        {sortIndicator && (
          <span className="text-forest-600 font-semibold">{sortIndicator}</span>
        )}

        {/* Filter button */}
        {isFilterable(column) && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setOpenFilter(openFilter === column.key ? null : column.key);
            }}
            className={`ml-auto p-1 rounded hover:bg-sage-100 transition-colors ${
              hasFilter ? 'text-forest-600' : 'text-sage-400'
            }`}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
          </button>
        )}

        {/* Filter popover */}
        {openFilter === column.key && (
          <FilterPopover
            column={column}
            value={filters[column.key] ?? null}
            discoveredValues={discoveredValues[column.key]}
            onChange={(value) => onFilter(column.key, value)}
            onClose={() => setOpenFilter(null)}
          />
        )}
      </div>

      {/* Resize handle */}
      {column.resizable !== false && (
        <div
          className={`absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-forest-400 ${
            resizing === column.key ? 'bg-forest-500' : ''
          }`}
          onMouseDown={(e) => onResizeStart(e, column.key)}
        />
      )}
    </th>
  );
}

export function DataTableHeader<T>({
  columns,
  columnOrder,
  columnWidths,
  hiddenColumns,
  sortConfig,
  filters,
  discoveredValues,
  selectable,
  allSelected,
  someSelected,
  onSelectAll,
  onSort,
  onFilter,
  onColumnResize,
  onColumnOrderChange,
}: DataTableHeaderProps<T>) {
  const [resizing, setResizing] = useState<string | null>(null);
  const [openFilter, setOpenFilter] = useState<string | null>(null);
  const startX = useRef(0);
  const startWidth = useRef(0);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Get visible columns in order
  const visibleColumns = columnOrder
    .map((key) => columns.find((c) => c.key === key))
    .filter((c): c is DataTableColumn<T> => c != null && !hiddenColumns.has(c.key));

  // Get visible column keys for DnD (cast to string[] for DnD compatibility)
  const visibleColumnKeys = visibleColumns.map((c) => String(c.key));

  // Handle drag end
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const activeId = String(active.id);
        const overId = String(over.id);
        const oldIndex = visibleColumnKeys.indexOf(activeId);
        const newIndex = visibleColumnKeys.indexOf(overId);
        const newVisibleOrder = arrayMove(visibleColumnKeys, oldIndex, newIndex);

        onColumnOrderChange?.(newVisibleOrder);
      }
    },
    [visibleColumnKeys, onColumnOrderChange]
  );

  // Handle resize start
  const handleResizeStart = useCallback(
    (e: React.MouseEvent, columnKey: string) => {
      e.preventDefault();
      setResizing(columnKey);
      startX.current = e.clientX;
      startWidth.current = columnWidths[columnKey] ?? 150;
    },
    [columnWidths]
  );

  // Handle resize move
  useEffect(() => {
    if (!resizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const diff = e.clientX - startX.current;
      const column = columns.find((c) => c.key === resizing);
      const minWidth = column?.minWidth ?? 80;
      const newWidth = Math.max(minWidth, startWidth.current + diff);
      onColumnResize(resizing, newWidth);
    };

    const handleMouseUp = () => {
      setResizing(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizing, columns, onColumnResize]);

  return (
    <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
      <tr className="border-b border-sage-200">
        {/* Selection column */}
        {selectable && (
          <th className="w-12 p-3 text-center">
            <input
              type="checkbox"
              checked={allSelected}
              ref={(el) => {
                if (el) el.indeterminate = (someSelected ?? false) && !allSelected;
              }}
              onChange={onSelectAll}
              className="rounded border-sage-300 text-forest-600 focus:ring-forest-500"
            />
          </th>
        )}

        {/* Sortable data columns */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          // DndContext renders hidden <div>s (screen-reader text); a <div> is invalid inside <tr>,
          // so portal them to <body>.
          accessibility={{ container: document.body }}
        >
          <SortableContext
            items={visibleColumnKeys}
            strategy={horizontalListSortingStrategy}
          >
            {visibleColumns.map((column) => (
              <SortableColumnHeader
                key={column.key}
                column={column}
                columnWidths={columnWidths}
                sortConfig={sortConfig}
                filters={filters}
                discoveredValues={discoveredValues}
                openFilter={openFilter}
                setOpenFilter={setOpenFilter}
                onSort={onSort}
                onFilter={onFilter}
                onResizeStart={handleResizeStart}
                resizing={resizing}
              />
            ))}
          </SortableContext>
        </DndContext>
      </tr>
    </thead>
  );
}
